import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ApplicationPayload, ApplyService } from '../services/apply.service';
import { SelectedCVData } from '../steps/upload-cv/upload-cv.component';
import { JobListService } from '../../../services/job-list.service';

export interface JobQuestion {
  questionId: string;
  question: string;
  required: boolean;
  displayOrder: number;
}

@Component({
  selector: 'app-apply-layout',
  templateUrl: './apply-layout.component.html',
  styleUrls: ['./apply-layout.component.css'],
})
export class ApplyLayoutComponent implements OnInit {
  steps: MenuItem[] = [];
  activeIndex = 0;
  isSubmitting = false;
  errorMessage = '';
  showSuccessPopup = false;
  isFetchingJob = false;

  jobTitle = '';
  companyName = '';
  companyLogo = '';
  companyInitials = '';
  companyId = '';
  jobPostId = '';
  questions: JobQuestion[] = [];

  selectedCVType: 'upload' | 'current' | null = null;

  private answersPayload: { questionId: string; question: string; answer: string }[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private applyJobService: ApplyService,
    private jobListService: JobListService
  ) {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as any;

    if (state) {
      this.jobTitle = state.jobTitle ?? '';
      this.companyName = state.companyName ?? '';
      this.companyLogo = state.companyLogo ?? '';
      this.companyInitials = state.companyInitials ?? '';
      this.companyId = state.companyId ?? '';
      this.jobPostId = state.jobPostId ?? '';

      this.questions = (state.questions ?? []).map((q: any) => ({
        questionId: q.questionId,
        question: q.question,
        required: q.required,
        displayOrder: q.displayOrder,
      }));
    }
  }

  ngOnInit(): void {
    if (!this.jobTitle && this.jobPostId) {
      this.fetchJobDetails();
    } else if (!this.jobPostId) {
      this.route.params.subscribe(params => {
        this.jobPostId = params['id'] ?? '';
        if (this.jobPostId && !this.jobTitle) {
          this.fetchJobDetails();
        } else {
          this.buildSteps();
        }
      });
    } else {
      this.buildSteps();
    }

    this.translate.onLangChange.subscribe(() => {
      if (!this.isFetchingJob) this.buildSteps();
    });
  }

  private fetchJobDetails(): void {
    this.isFetchingJob = true;
    this.jobListService.getJobById(this.jobPostId).subscribe({
      next: (job: any) => {
        this.jobTitle = job.title ?? '';
        this.companyName = job.companyName ?? job.company ?? '';
        this.companyLogo = job.confidential ? '' : (job.companyLogoUrl ?? '');
        this.companyInitials = job.companyInitial ?? '';
        this.companyId = job.confidential ? '' : (job.companyId ?? '');
        this.questions = (job.questions ?? []).map((q: any) => ({
          questionId: q.questionId,
          question: q.question,
          required: q.required,
          displayOrder: q.displayOrder,
        }));
        this.isFetchingJob = false;
        this.buildSteps();
      },
      error: () => {
        this.isFetchingJob = false;
        this.buildSteps();
      }
    });
  }

  private buildSteps(): void {
    this.translate.get(['jobApply.answerQuestions', 'jobApply.confirmYourCV']).subscribe((t) => {
      this.steps = [];
      if (this.questions && this.questions.length > 0) {
        this.steps.push({ label: t['jobApply.answerQuestions'], id: 'questions' });
      }
      this.steps.push({ label: t['jobApply.confirmYourCV'], id: 'cv' });
    });
  }

  onCVTypeChanged(type: 'upload' | 'current'): void {
    this.selectedCVType = type;
  }

  onAnswersSubmitted(answers: { questionId: string; question: string; answer: string }[]): void {
    this.answersPayload = answers;
    this.activeIndex++;
  }

  onCVSubmitted(cvData: SelectedCVData): void {
    this.submitApplication(cvData);
  }

  private submitApplication(cvData: SelectedCVData): void {
    if (!this.jobPostId) {
      console.error('❌ jobPostId is missing');
      return;
    }

    this.isSubmitting = true;

    const payload: ApplicationPayload = {
      coverLetter: '',
      answers: this.answersPayload,
      ...(cvData.type === 'generated' && cvData.cvPath && { cvPath: cvData.cvPath }),
    };

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📤 Submitting Application');
    console.log('🆔 Job Post ID:', this.jobPostId);
    console.log('📁 CV Type:', cvData.type);
    if (cvData.type === 'uploaded') {
      console.log('📎 CV File Name:', cvData.file?.name);
      console.log('📎 CV File Size:', cvData.file?.size, 'bytes');
      console.log('📎 CV File Type:', cvData.file?.type);
    } else {
      console.log('🔗 CV Path:', cvData.cvPath);
    }
    console.log('📋 Payload:', JSON.stringify(payload, null, 2));
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const request$ = cvData.type === 'uploaded' && cvData.file
      ? this.applyJobService.applyWithCvFile(this.jobPostId, payload, cvData.file)
      : this.applyJobService.applyWithCvPath(this.jobPostId, payload);

    request$.subscribe({
      next: (res) => {
        console.log('✅ Application submitted successfully:', res);
        this.isSubmitting = false;
        this.errorMessage = '';
        this.showSuccessPopup = true;
      },
      error: (err) => {
        console.error('❌ Application error status:', err?.status);
        console.error('❌ Application error body:', err?.error);
        this.isSubmitting = false;
        this.errorMessage = err?.error?.message || 'Failed to submit application. Please try again.';
      }
    });
  }

  closeSuccessAndNavigate(): void {
    this.showSuccessPopup = false;
    this.router.navigate(['/']);
  }

  previous(): void {
    if (this.activeIndex > 0) this.activeIndex--;
  }

  cancel(): void {
    this.router.navigate(['/jobs']);
  }

  goBack(): void {
    history.back();
  }

  viewCompany(): void {
    if (this.companyId) this.router.navigate(['/company-profile', this.companyId]);
  }
}